import React from 'react';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { FolderOpen, Image, Upload, Download, Trash2, FileText } from 'lucide-react';
import { COMPANY } from '../../../config/company';

// Helper function to safely get photo path (handles both string and object formats)
const getPhotoPath = (photo) => {
  if (!photo) return '';
  if (typeof photo === 'string') return photo;
  if (typeof photo === 'object') {
    // For GridFS photos, return the gridfs_id as the path
    if (photo.gridfs_id) return photo.gridfs_id;
    return photo.path || photo.url || photo.id || '';
  }
  return '';
};

// Helper function to get photo filename for display
const getPhotoFilename = (photo) => {
  if (!photo) return 'Unknown';
  // For GridFS photos with original_filename
  if (typeof photo === 'object' && photo.original_filename) {
    return photo.original_filename;
  }
  const path = getPhotoPath(photo);
  if (!path) return 'Unknown';
  const parts = path.split('/');
  const filename = parts[parts.length - 1] || parts[0] || 'Unknown';
  return filename.replace(/^[a-f0-9-]+_/, '');
};

const LeadFilesTab = ({
  language,
  lead,
  handleInputChange,
  // Document functions
  fileInputRef,
  handleDocumentUpload,
  getDocumentName,
  getDocumentUrl,
  formatFileSize,
  deleteDocument,
  // Photo functions
  photoInputRef,
  handlePhotoUpload,
  getPhotoUrl,
  deletePhoto
}) => {
  return (
    <>
      {/* Documents Section */}
      <Card data-testid="lead-documents-section">
        <CardHeader style={{ backgroundColor: COMPANY.primaryColor }} className="text-white rounded-t-lg">
          <CardTitle className="flex items-center">
            <FolderOpen className="mr-2 h-5 w-5" />
            {language === 'es' ? 'Documentos' : 'Documents'}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <input 
            type="file" 
            ref={fileInputRef} 
            onChange={handleDocumentUpload} 
            className="hidden" 
            accept=".pdf,application/pdf" 
          />
          <Button 
            onClick={() => fileInputRef.current?.click()} 
            variant="outline" 
            className="w-full border-dashed border-2 mb-4"
            data-testid="upload-document-btn"
          >
            <Upload className="mr-2 h-4 w-4" />
            {language === 'es' ? 'Subir PDF' : 'Upload PDF'}
          </Button>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {(lead.documents || []).map((doc, i) => (
              <div 
                key={typeof doc === 'object' ? doc.id : i} 
                className="flex items-center justify-between bg-gray-50 rounded-lg p-3 hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center flex-1 min-w-0">
                  <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center mr-3">
                    <FileText className="h-5 w-5 text-red-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 truncate" title={getDocumentName(doc)}>
                      {getDocumentName(doc)}
                    </p>
                    {typeof doc === 'object' && doc.file_size && (
                      <p className="text-xs text-gray-500">{formatFileSize(doc.file_size)}</p>
                    )}
                  </div>
                </div>
                <div className="flex gap-1 ml-2">
                  <a href={getDocumentUrl(doc)} target="_blank" rel="noreferrer" download={getDocumentName(doc)}>
                    <Button size="sm" variant="outline" className="h-8 px-2">
                      <Download className="h-4 w-4" />
                    </Button>
                  </a>
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    className="text-red-400 hover:text-red-600 h-8 px-2" 
                    onClick={() => deleteDocument(doc)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
            {(!lead.documents || lead.documents.length === 0) && (
              <p className="text-gray-400 text-center py-4 text-sm">
                {language === 'es' ? 'Sin documentos' : 'No documents yet'}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Photos Section */}
      <Card data-testid="lead-photos-section">
        <CardHeader style={{ backgroundColor: COMPANY.primaryColor }} className="text-white rounded-t-lg">
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center">
              <Image className="mr-2 h-5 w-5" />
              {language === 'es' ? 'Fotos' : 'Photos'}
            </span>
            <label className="flex items-center text-sm font-normal">
              <input 
                type="checkbox" 
                checked={lead.show_photos_in_estimate !== false}
                onChange={(e) => handleInputChange('show_photos_in_estimate', e.target.checked)}
                className="mr-2 h-4 w-4"
              />
              {language === 'es' ? 'En PDF' : 'In PDF'}
            </label>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <input 
            type="file" 
            ref={photoInputRef} 
            onChange={handlePhotoUpload} 
            className="hidden" 
            accept="image/*" 
          />
          <Button 
            onClick={() => photoInputRef.current?.click()} 
            variant="outline" 
            className="w-full border-dashed border-2 mb-4"
            data-testid="upload-photo-btn"
          >
            <Image className="mr-2 h-4 w-4" />
            {language === 'es' ? 'Subir Foto' : 'Upload Photo'}
          </Button>
          <div className="grid grid-cols-2 gap-3 max-h-64 overflow-y-auto">
            {(lead.photos || []).map((photo, i) => {
              const photoUrl = getPhotoUrl(photo);
              if (!photoUrl) return null;
              return (
                <div key={i} className="relative group border rounded-lg p-2 bg-gray-50">
                  <img 
                    src={photoUrl} 
                    alt={`Photo ${i+1}`} 
                    className="w-full h-24 object-cover rounded-lg mb-1" 
                    onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                  />
                  <div className="w-full h-24 bg-gray-200 rounded-lg items-center justify-center text-gray-500 hidden">
                    <FileText className="h-6 w-6" />
                  </div>
                  <p className="text-xs text-gray-600 truncate">{getPhotoFilename(photo)}</p>
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    className="absolute top-2 right-2 bg-red-500 text-white h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity" 
                    onClick={() => deletePhoto(photo)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              );
            })}
            {(!lead.photos || lead.photos.length === 0) && (
              <p className="text-gray-400 text-center py-4 text-sm col-span-2">
                {language === 'es' ? 'Sin fotos' : 'No photos yet'}
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </>
  );
};

export default LeadFilesTab;
